import React, { useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { Alert, Col, Form, FormGroup, Input, Label, Row, Spinner } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { apiDexExport } from '~constants/api';
import { getErrorMessage } from '~libs/axiosWrapper';
import { dateDisplayFormat } from '~libs/dayjs';

import { Button, ContainerHeader, ErrorResponse } from '~components';
import { DateInput } from '~components/form-fields/inputs';
import { Stepper } from '~components/wizard-form';
import { setTitle } from '~main/actions/login';

import { useLazyGetDexPreviewQuery } from '../actions/dex';
import { DexExportParams } from '../types';
import DexPreviewPanel from './DexPreviewPanel';

type Step = 'configure' | 'preview';

interface FormValues {
  date_from: string;
  date_to: string;
  de_identify: boolean;
}

const INITIAL_VALUES: FormValues = { date_from: '', date_to: '', de_identify: false };

const validationSchema = Yup.object({
  date_from: Yup.string()
    .required('From date is required')
    .test('date-range', 'From date must be before to date', function (date_from) {
      const { date_to } = this.parent as FormValues;
      return !date_from || !date_to || date_from <= date_to;
    }),
  date_to: Yup.string().required('To date is required'),
});

const DexExportPage: React.FC = () => {
  useEffect(() => {
    setTitle('DEX Export');
  }, []);

  const [step, setStep] = useState<Step>('configure');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const [triggerPreview, { data: preview, isFetching, error: previewError }] = useLazyGetDexPreviewQuery();

  const buildParams = (values: FormValues): DexExportParams => ({
    date_from: values.date_from,
    date_to: values.date_to,
    ...(values.de_identify ? { de_identify: true } : {}),
  });

  const formik = useFormik<FormValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: async (values) => {
      try {
        await triggerPreview(buildParams(values)).unwrap();
        setStep('preview');
      } catch {
        // previewError state surfaces the message below the form
      }
    },
  });

  const handleDateChange = (id: string, value: string | null) => {
    formik.setFieldValue(id, value ?? '');
    formik.setFieldTouched(id, true, false);
  };

  const handleBack = () => {
    setStep('configure');
    setDownloadError(null);
    setDownloadSuccess(false);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadError(null);
    setDownloadSuccess(false);
    try {
      const response = await axios.get(apiDexExport, {
        params: buildParams(formik.values),
        responseType: 'blob',
      });
      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : 'dex-export.xml';
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
      setDownloadSuccess(true);
    } catch (err) {
      const axiosErr = err as AxiosError;
      let message = 'Download failed. Please try again.';
      const data = axiosErr.response?.data;
      if (data instanceof Blob) {
        try {
          message = getErrorMessage(JSON.parse(await data.text()));
        } catch {
          // keep fallback message - error body wasn't valid JSON
        }
      } else if (data) {
        message = getErrorMessage(data);
      }
      setDownloadError(message);
    } finally {
      setIsDownloading(false);
    }
  };

  const readinessUrl = `/dex/readiness?date_from=${encodeURIComponent(formik.values.date_from)}&date_to=${encodeURIComponent(formik.values.date_to)}`;

  return (
    <div className="dex-export-page p-2" data-cy="dex-export-page">
      <ContainerHeader dataCy="dex-export-header">DEX Export (Beta)</ContainerHeader>

      <div className="wizard-form">
        <Stepper
          currentStep={step === 'configure' ? 0 : 1}
          steps={[
            { title: 'Configure', step: 0, fields: {} },
            { title: 'Preview', step: 1, fields: {} },
          ]}
        />

        <div className="d-flex flex-column flex-grow-1">
          <div className="wizard-form-content">
            {step === 'configure' && (
              <>
                <div className="p-2 ps-4 pe-4">
                  <p className="text-muted mb-3">
                    Select a date range and download the DEX XML file for submission to DSS.
                  </p>

                  <Form onSubmit={formik.handleSubmit}>
                    <Row>
                      <Col md={3}>
                        <FormGroup>
                          <Label for="date_from">From Date</Label>
                          <DateInput
                            type="date"
                            id="date_from"
                            name="date_from"
                            value={formik.values.date_from}
                            onChange={handleDateChange}
                            dataCy="dex-from-date"
                            maxDate={formik.values.date_to || undefined}
                            displayFormat={dateDisplayFormat}
                          />
                          {formik.touched.date_from && formik.errors.date_from && (
                            <div className="invalid-feedback d-block" data-cy="dex-from-date-error">
                              {formik.errors.date_from}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label for="date_to">To Date</Label>
                          <DateInput
                            type="date"
                            id="date_to"
                            name="date_to"
                            value={formik.values.date_to}
                            onChange={handleDateChange}
                            dataCy="dex-to-date"
                            minDate={formik.values.date_from || undefined}
                            displayFormat={dateDisplayFormat}
                          />
                          {formik.touched.date_to && formik.errors.date_to && (
                            <div className="invalid-feedback d-block" data-cy="dex-to-date-error">
                              {formik.errors.date_to}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="dex-de-identify"
                        name="de_identify"
                        className="form-check-input"
                        checked={formik.values.de_identify}
                        onChange={formik.handleChange}
                        data-cy="dex-de-identify-checkbox"
                      />
                      <Label check for="dex-de-identify">
                        De-identify export (omit client names and addresses, use SLK only)
                      </Label>
                    </FormGroup>
                  </Form>

                  {previewError && (
                    <ErrorResponse message={getErrorMessage(previewError)} className="mt-3" dataCy="dex-preview-error" />
                  )}
                </div>

                <div className="d-flex justify-content-end mt-auto pt-3">
                  <Button
                    color="success"
                    onClick={() => formik.handleSubmit()}
                    disabled={isFetching}
                    data-cy="dex-preview-button"
                  >
                    {isFetching ? <Spinner size="sm" /> : 'Preview'}
                  </Button>
                </div>
              </>
            )}

            {step === 'preview' && preview && (
              <>
                <div className="p-2 ps-4 pe-4">
                  <DexPreviewPanel preview={preview} data-cy="dex-preview-panel" />

                  {preview.issues.length > 0 && (
                    <Alert color="warning" className="mt-3" data-cy="dex-issues-cta">
                      {preview.issues.length} appointment{preview.issues.length !== 1 ? 's have' : ' has'} data quality
                      issues that may affect the export. Missing Stage 3 session data can be reviewed in the{' '}
                      <a href={readinessUrl} target="_blank" rel="noopener noreferrer">
                        DEX Readiness Report
                      </a>
                      . Client ID and package configuration issues must be resolved separately.
                    </Alert>
                  )}
                </div>

                <div className="d-flex justify-content-end mt-auto pt-3">
                  <Button color="secondary" className="me-2" onClick={handleBack} data-cy="dex-back-button">
                    Previous
                  </Button>
                  <Button color="success" onClick={handleDownload} disabled={isDownloading} data-cy="dex-download-button">
                    {isDownloading ? <Spinner size="sm" /> : 'Download XML'}
                  </Button>
                </div>

                {downloadError && <ErrorResponse message={downloadError} className="mt-3" dataCy="dex-download-error" />}

                {downloadSuccess && (
                  <Alert color="success" className="mt-3" data-cy="dex-download-success">
                    Download started successfully.
                  </Alert>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DexExportPage;
